import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { SeasonStatus } from '@/types/admin';

const STATUS_OPTIONS: { value: SeasonStatus; label: string }[] = [
  { value: 'UPCOMING', label: 'Próxima' },
  { value: 'ACTIVE', label: 'Ativa' },
  { value: 'COMPLETED', label: 'Finalizada' },
];

const AdminSeasonFormPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isEditing = !!id;

  const [form, setForm] = useState({
    name: '',
    description: '',
    image_url: '',
    phase: '',
    status: 'UPCOMING' as SeasonStatus,
    start_date: '',
    end_date: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEditing) loadSeason();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSeason = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const season = await adminService.getSeason(id);
      setForm({
        name: season.name,
        description: season.description || '',
        image_url: season.image_url || '',
        phase: season.phase || '',
        status: season.status,
        start_date: season.start_date ? season.start_date.slice(0, 16) : '',
        end_date: season.end_date ? season.end_date.slice(0, 16) : '',
      });
    } catch {
      showToast('Erro ao carregar temporada', 'error');
      navigate('/admin/temporadas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast('Nome é obrigatório', 'warning');
      return;
    }
    if (!form.start_date) {
      showToast('Data de início é obrigatória', 'warning');
      return;
    }
    if (!form.end_date) {
      showToast('Data de fim é obrigatória', 'warning');
      return;
    }
    if (new Date(form.end_date) <= new Date(form.start_date)) {
      showToast('Data de fim deve ser posterior à data de início', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        image_url: form.image_url,
        phase: form.phase,
        status: form.status,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
      };

      if (isEditing && id) {
        await adminService.updateSeason(id, payload);
      } else {
        await adminService.createSeason(payload);
      }
      navigate('/admin/temporadas');
    } catch {
      showToast('Erro ao salvar temporada', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-10 text-content-muted">Carregando...</div>;
  }

  return (
    <div className="p-6 lg:p-10 max-w-3xl">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/admin/temporadas')} className="text-content-muted hover:text-content-primary">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Editar Temporada' : 'Nova Temporada'}</h1>
      </div>

      <div className="space-y-6">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium mb-1">Nome *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            placeholder="Nome da temporada"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-1">Descrição</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none resize-none"
            placeholder="Descrição da temporada"
          />
        </div>

        {/* Phase + Status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Fase</label>
            <input
              type="text"
              value={form.phase}
              onChange={(e) => setForm({ ...form, phase: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
              placeholder="Ex: Fase 1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as SeasonStatus })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Image URL */}
        <div>
          <label className="block text-sm font-medium mb-1">URL da Imagem</label>
          <input
            type="url"
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            placeholder="https://..."
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Data Início *</label>
            <input
              type="datetime-local"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Data Fim *</label>
            <input
              type="datetime-local"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            />
          </div>
        </div>

        {/* Save */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Temporada'}
          </button>
          <button
            onClick={() => navigate('/admin/temporadas')}
            className="px-6 py-2.5 rounded-xl font-medium border border-theme-border text-content-muted hover:text-content-primary transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSeasonFormPage;
